import { getLanguages, getTranslations } from 'core/service/user-language/translations.service';
import { saveUserLanguage } from 'core/service/user-language/user-language.service';
import { DEFAULT_LANGUAGE } from 'core/constants/constants';
import { AppGlobalContext, AppGlobalContextType } from 'core/contexts/app.provider.context';
import { i18nProvider } from 'core/i18n/i18n';
import { languageKeys } from 'core/query-client/keys/language.keys';
import { fetchQueryCached } from 'core/query-client/query-client';
import { showErrorNotification } from 'core/utils/notification-service';
import { useContext, useState } from 'react';
import { resolveSupportedLanguage } from './use-user-language-manager.business';

const resolveLanguage = async (language: string): Promise<string> => {
    try {
        const supportedLanguages = await fetchQueryCached(languageKeys.all, () => getLanguages(), {
            staleTime: Infinity,
            retry: false,
        });
        return resolveSupportedLanguage(language, supportedLanguages);
    } catch {
        return language;
    }
};

type UseUserLanguageManagerParams = {
    userPreferencesId: string;
};

// userPreferencesId arrives as an argument, not through useUserPreferencesContext:
// UserPreferencesProvider calls this hook above its own Provider, so reading the
// context here would always yield the default rather than the provider's state.
// useUserPageSizerManager already takes it the same way.
export const useUserLanguageManager = ({ userPreferencesId }: UseUserLanguageManagerParams) => {
    const { setIsPageLoading } = useContext<AppGlobalContextType>(AppGlobalContext);
    const [userLanguage, setUserLanguage] = useState<string>(DEFAULT_LANGUAGE);

    const updateUserLanguage = async (language: string) => {
        setIsPageLoading(true);
        try {
            const languageToSave = await resolveLanguage(language);
            await saveUserLanguage(userPreferencesId, languageToSave);
            await applyResolvedLanguage(languageToSave);
        } catch {
            showErrorNotification('commons.components.language-picker.notification.error');
        }
        setIsPageLoading(false);
    };

    const applyResolvedLanguage = async (language: string) => {
        let languageToApply = language;
        try {
            const translations = await getTranslations(languageToApply);
            i18nProvider(languageToApply, translations);
        } catch (error) {
            console.error(`Failed to resolve language. Falling back to default language "${DEFAULT_LANGUAGE}".`, error);
            languageToApply = DEFAULT_LANGUAGE;
            i18nProvider(DEFAULT_LANGUAGE);
        }

        setUserLanguage(languageToApply);
    };

    const applyUserLanguage = async (language: string) => {
        await applyResolvedLanguage(await resolveLanguage(language));
    };

    return { userLanguage, applyUserLanguage, updateUserLanguage };
};
